import { ComponentUsageError } from 'BomdisoftComponents/errors/index';
import type { TabOverflowLabels } from 'BomdisoftComponents/tab/render';
import { type SupportedLanguage, scrollTabsBackT, scrollTabsForwardT } from '@bomdisoft/translations';

export const tabOverflowLabels = (language: SupportedLanguage): TabOverflowLabels => ({ back: scrollTabsBackT[language], forward: scrollTabsForwardT[language] });

/** How much of the visible row one chevron press travels. A full width would
 *  land the reader on an unfamiliar strip with no shared label between it and
 *  where they were. */
const PAGE_FRACTION = 0.8;

/**
 * Which edges have more row behind them.
 *
 * Pure, and exported, because it is the one part of this hook a test can reach:
 * `scrollWidth` and `clientWidth` are both 0 without a layout engine, so the
 * measuring half can only be verified in a browser and the deciding half is
 * verified here.
 *
 * The 1px tolerance is not a rounding nicety. A row whose content is a
 * fractional pixel wider than its box is not scrollable by anything a reader
 * can perceive, and without the slack it would light a chevron that travels
 * nowhere and a fade over a label that is already fully visible.
 */
export const overflowEdges = (scrollLeft: number, scrollWidth: number, clientWidth: number): { end: boolean; start: boolean } => ({
  end: Math.abs(scrollLeft) < scrollWidth - clientWidth - 1,
  start: Math.abs(scrollLeft) > 1,
});

export const pageTravel = (clientWidth: number, direction: 1 | -1, isRtl: boolean): number => Math.round(clientWidth * PAGE_FRACTION) * direction * (isRtl ? -1 : 1);

const SELECTED_TAB = '[role="tab"][aria-selected="true"]';

const setFlag = (element: Element, name: string, isOn: boolean) => {
  if (isOn) element.setAttribute(name, 'true');
  else element.removeAttribute(name);
};

export const delegateTabOverflow = (frame: HTMLElement, { selectedSelector = SELECTED_TAB }: { selectedSelector?: string } = {}) => {
  const view = frame.ownerDocument.defaultView;
  if (view === null) throw new ComponentUsageError('detached_root', 'delegateTabOverflow needs a frame that belongs to a window');
  const row = frame.querySelector<HTMLElement>('.bomdisoft-tab-overflow-scroller');
  let isSettled = false;

  const sync = () => {
    if (!row) return;
    const { end, start } = overflowEdges(row.scrollLeft, row.scrollWidth, row.clientWidth);
    setFlag(frame, 'data-more-end', end);
    setFlag(frame, 'data-more-start', start);
  };

  const reveal = () => {
    const active = row?.querySelector<HTMLElement>(selectedSelector);
    if (active) {
      const isReduced = view.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      active.scrollIntoView({ behavior: isSettled && !isReduced ? 'smooth' : 'auto', block: 'nearest', inline: 'nearest' });
      isSettled = true;
    }
    sync();
  };

  const page = (direction: 1 | -1) => {
    if (row) row.scrollBy({ behavior: 'smooth', left: pageTravel(row.clientWidth, direction, view.getComputedStyle(row).direction === 'rtl') });
  };

  const handleClick = (event: MouseEvent) => {
    const nudge = event.target instanceof view.Element ? event.target.closest('.bomdisoft-tab-overflow-nudge') : null;
    if (nudge === null || !frame.contains(nudge)) return;
    page(nudge.closest('.bomdisoft-tab-overflow-start') ? -1 : 1);
  };

  const resizes = 'ResizeObserver' in view ? new view.ResizeObserver(sync) : null;
  if (row && resizes) {
    resizes.observe(row);
    for (const child of row.children) resizes.observe(child);
  }
  const mutations = new view.MutationObserver(sync);
  if (row) mutations.observe(row, { childList: true, subtree: true });
  row?.addEventListener('scroll', sync);
  frame.addEventListener('click', handleClick);
  reveal();

  return {
    page,
    release: () => {
      resizes?.disconnect();
      mutations.disconnect();
      row?.removeEventListener('scroll', sync);
      frame.removeEventListener('click', handleClick);
    },
    reveal,
    sync,
  };
};

export const delegateTabs = (list: HTMLElement, { onSelect }: { onSelect?: (tab: HTMLElement, index: number) => void } = {}) => {
  const view = list.ownerDocument.defaultView;
  if (view === null) throw new ComponentUsageError('detached_root', 'delegateTabs needs a tab list that belongs to a window');
  const tabs = () => [...list.querySelectorAll<HTMLElement>('[role="tab"]')];

  const select = (index: number, shouldFocus: boolean) => {
    const all = tabs();
    const chosen = all[index];
    if (!chosen) return;
    all.forEach((tab, at) => {
      tab.setAttribute('aria-selected', String(at === index));
      tab.tabIndex = at === index ? 0 : -1;
    });
    const panelId = chosen.getAttribute('aria-controls');
    const panel = panelId ? list.ownerDocument.getElementById(panelId) : null;
    if (panel && chosen.id) panel.setAttribute('aria-labelledby', chosen.id);
    if (shouldFocus) {
      chosen.focus();
      chosen.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
    onSelect?.(chosen, index);
  };

  const handleClick = (event: MouseEvent) => {
    const tab = event.target instanceof view.Element ? event.target.closest<HTMLElement>('[role="tab"]') : null;
    if (tab !== null && list.contains(tab)) select(tabs().indexOf(tab), false);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const all = tabs();
    const at = all.findIndex(tab => tab.getAttribute('aria-selected') === 'true');
    const moves: Record<string, number> = { ArrowLeft: at - 1, ArrowRight: at + 1, End: all.length - 1, Home: 0 };
    const next = moves[event.key];
    if (next === undefined) return;
    event.preventDefault();
    select((next + all.length) % all.length, true);
  };

  list.addEventListener('click', handleClick);
  list.addEventListener('keydown', handleKeyDown);
  return () => {
    list.removeEventListener('click', handleClick);
    list.removeEventListener('keydown', handleKeyDown);
  };
};
